import React, {
  Dispatch,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom/client';
import classNames from 'classnames';
import {
  defaultSizesByFieldType,
  fieldShapes,
} from 'Components/Interact/common/fieldShapes';
import { useInteractMedia } from 'Hooks/Document';
import { DocumentFieldTypes } from 'Interfaces/DocumentFields';
import { checkIfDateOrText } from 'Utils/functions';

import FieldBarItem from './FieldBarItem';
import FieldItemView from './FieldItemView';
import InteractPreparing from './InteractPreparing';

interface InteractFieldBarProps {
  className?: string;
  fieldRootRef: MutableRefObject<ReactDOM.Root | null>;
  containerRef: RefObject<HTMLDivElement>;
  fieldShapeContainerRef: MutableRefObject<HTMLDivElement | null>;
  documentScale: number;
  selectedScale: number;
  selectedFieldType: DocumentFieldTypes | undefined;
  isDocumentFirstAndDisabled: boolean;
  areFieldsSelecting?: boolean;
  isInteractPreparing: boolean;
  loadedPagesCount: number;
  totalPagesCount: number;
  renderActionForm?: () => ReactNode;
  renderTopFields?: () => ReactNode;
  onFieldShapeRemove: () => void;
  setFieldShapeExist: Dispatch<SetStateAction<boolean>>;
  setSelectedFieldType: Dispatch<SetStateAction<DocumentFieldTypes | undefined>>;
}

const InteractFieldBar = ({
  children,
  className,
  fieldRootRef,
  containerRef,
  fieldShapeContainerRef,
  documentScale,
  selectedScale,
  selectedFieldType,
  isDocumentFirstAndDisabled,
  areFieldsSelecting,
  isInteractPreparing,
  loadedPagesCount,
  totalPagesCount,
  renderActionForm,
  renderTopFields,
  onFieldShapeRemove,
  setFieldShapeExist,
  setSelectedFieldType,
}: PropsWithChildren<InteractFieldBarProps>) => {
  const [isMobile] = useInteractMedia(['sm']);

  const handleFieldBarItemCursorMove = useCallback(
    (event, fieldType: DocumentFieldTypes) => {
      if (
        !containerRef.current ||
        !event.interaction.pointerIsDown ||
        event.interaction.interacting()
      ) {
        return;
      }
      const { clientX: pointerX, clientY: pointerY } = event;
      const { width, height } = defaultSizesByFieldType[fieldType];
      const draggable = document.createElement('div');
      containerRef.current.appendChild(draggable);
      const isDateOrText = checkIfDateOrText(fieldType);

      ReactDOM.createRoot(draggable).render(
        <FieldItemView
          data-clone="true"
          fieldType={fieldType}
          className={fieldType}
          style={{
            userSelect: 'none',
            position: 'fixed',
            left: pointerX - (width * documentScale) / 2,
            top: pointerY - (height * documentScale) / 2,
            [isDateOrText ? 'minWidth' : 'width']: width,
            [isDateOrText ? 'minHeight' : 'height']: height,
            transform: `scale(${documentScale})`,
            transformOrigin: 'top left',
          }}
          selectedScale={0.9 * documentScale}
        />,
      );

      setTimeout(() => {
        event.bubbles = true;
        const dragStartEvent = new PointerEvent('pointerdown', event);

        draggable.firstChild?.dispatchEvent(dragStartEvent);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentScale],
  );

  const handleCreateFieldShape = useCallback(
    (
      event: ReactMouseEvent<HTMLLIElement>,
      type: DocumentFieldTypes,
      renderShape?: boolean,
    ) => {
      event.stopPropagation();

      if (fieldRootRef.current && fieldShapeContainerRef.current && !renderShape) {
        return fieldRootRef.current.unmount();
      }

      onFieldShapeRemove();

      if (fieldShapeContainerRef.current && renderShape && type !== selectedFieldType) {
        fieldShapeContainerRef.current.style.top = `${event.clientY}px`;
        fieldShapeContainerRef.current.style.left = `${event.clientX}px`;

        if (!isMobile) {
          fieldRootRef.current = ReactDOM.createRoot(fieldShapeContainerRef.current);
          fieldRootRef.current.render(
            <FieldItemView
              data-clone="true"
              fieldType={type}
              className={type}
              style={{
                userSelect: 'none',
                transform: `scale(${documentScale})`,
                transformOrigin: 'top left',
                width: defaultSizesByFieldType[type].width,
                height: defaultSizesByFieldType[type].height,
              }}
              selectedScale={selectedScale}
            />,
          );
        }

        setSelectedFieldType(type);
        setFieldShapeExist(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentScale, onFieldShapeRemove, isMobile, selectedFieldType, selectedScale],
  );

  return (
    <aside className={classNames('interactModal__fieldBar', className)}>
      {renderActionForm?.()}
      {areFieldsSelecting && (
        <div className="interactModal__fieldBar-fieldWrapper">
          {renderTopFields?.()}
          <p className="interactModal__fieldBar-fieldTitle">Fields</p>
          <ul className="interactModal__fieldBar-fieldList">
            {fieldShapes.map(fieldShape => (
              <FieldBarItem
                key={fieldShape.type}
                selectedFieldType={selectedFieldType}
                handleSelectField={setSelectedFieldType}
                fieldShape={fieldShape}
                disabled={isInteractPreparing || isDocumentFirstAndDisabled}
                onCursorMove={handleFieldBarItemCursorMove}
                onClick={handleCreateFieldShape}
              />
            ))}
          </ul>
        </div>
      )}
      {isInteractPreparing && (
        <InteractPreparing count={loadedPagesCount} total={totalPagesCount} />
      )}
      {children}
    </aside>
  );
};

export default InteractFieldBar;
